import React, { useCallback, useState } from 'react';

import { withGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useHistory } from 'react-router-dom';

import UserActions from '../actions/UserActions';
import utils from '../utils';

function LoginForm(props) {
  const [fields, setFields] = useState<{ username: string; password: string }>({ username: '', password: '' });
  const [errorMessage, setErrorMessage] = useState(null);
  const [isFormProcessing, setIsFormProcessing] = useState(false);

  const { googleReCaptchaProps, redirectTo, onSubmit, onSubmitError, onSubmitSuccess } = props;
  const history = useHistory();

  const setErrorState = useCallback((recentError, newError) => {
    setIsFormProcessing(false);
    setErrorMessage((newError || recentError)?.message || null);
  }, []);

  const handleVerifyRecaptcha = useCallback(async () => {
    const { executeRecaptcha } = googleReCaptchaProps;
    return await executeRecaptcha('login');
  }, [googleReCaptchaProps]);

  const performRedirect = useCallback(() => {
    history.push(redirectTo || '/');
  }, [history, redirectTo]);

  /* mapping helpers that utils.makeForm expected --------------------- */
  const mapFormFieldHandler = useCallback((element, tryMapField) => {
    const tryMapFormField = () => {
      switch (element.props.name) {
        case 'login':
        case 'username':
          tryMapField('username');
          break;
        case 'password':
          tryMapField('password');
          break;
        default:
          break;
      }
    };
    utils.mapFormField(element, tryMapFormField, undefined);
  }, []);

  const spIfHandler = useCallback(
    (action) => {
      switch (action) {
        case 'form.processing':
          return isFormProcessing;
        case 'form.error':
          return Boolean(errorMessage);
        default:
          return null;
      }
    },
    [isFormProcessing, errorMessage],
  );

  const spBindHandler = useCallback(
    (bind, element) => {
      if (bind === 'form.errorMessage') {
        const cls = element.props ? element.props.className : undefined;
        return <span className={cls}>{errorMessage}</span>;
      }
      return false;
    },
    [errorMessage],
  );

  const onFormSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      e.persist();

      // Clear any existing error and mark the form as busy
      setErrorState(null, null);
      setIsFormProcessing(true);

      // Collect values from the form
      const formData = new FormData(e.target);
      const submittedFormData = {
        username: formData.get('login') as string,
        password: formData.get('password') as string,
      };

      setFields({
        ...submittedFormData,
      });

      // Run reCAPTCHA
      let recaptchaResponse;
      try {
        recaptchaResponse = await handleVerifyRecaptcha();
      } catch (err) {
        console.error(err);
        setErrorState(new Error('Invalid Login Verification'), null);
      }

      // ------------------------------------------------------------------
      // Equivalent to the old `next` callback the class version passed around
      // ------------------------------------------------------------------
      const next = (err, data) => {
        if (err) {
          if (onSubmitError) {
            console.error(err);
            // Old code did: setErrorState.bind(this, err)
            return onSubmitError({ data, error: err }, (newErr) => setErrorState(err, newErr));
          }
          return setErrorState(err, null);
        }

        data = data || submittedFormData;

        UserActions.login(
          {
            login: data.username,
            password: data.password,
            recaptchaResponse,
          },
          (err2, result) => {
            if (err2) {
              console.error(err2);
              if (onSubmitError) {
                return onSubmitError({ data, error: err2 }, (newErr) => setErrorState(err2, newErr));
              }
              return setErrorState(err2, null);
            }

            if (onSubmitSuccess) {
              return onSubmitSuccess(
                { data, result },
                performRedirect, // no `bind` needed
              );
            }

            // Default success flow
            performRedirect();
          },
        );
      };

      if (onSubmit) {
        e.data = submittedFormData; // preserve public API
        onSubmit(e, next);
      } else {
        // IMPORTANT: always start fresh — do *not* carry forward a stale error
        next(null, submittedFormData);
      }
    },
    [handleVerifyRecaptcha, onSubmit, onSubmitError, onSubmitSuccess, performRedirect, setErrorState],
  );

  const formProps = utils.excludeProps(
    [
      'redirectTo',
      'hideSocial',
      'onSubmit',
      'onSubmitError',
      'onSubmitSuccess',
      'children',
      'staticContext',
      'googleReCaptchaProps',
    ],
    props,
  );

  return (
    <form onSubmit={onFormSubmit} {...formProps}>
      {utils.makeForm(
        /* utils.makeForm used to receive `this`; supply an object with the
           same surface (just state & props) so it keeps working. */
        { state: { fields, errorMessage, isFormProcessing }, props },
        mapFormFieldHandler,
        spIfHandler,
        spBindHandler,
      )}
    </form>
  );
}

export default withGoogleReCaptcha(LoginForm);
